import React, { useState, Fragment } from "react";
import { Link, withRouter, RouteComponentProps } from "react-router-dom";
import { UtilityFunctions } from "../../utils/UtilityFunctions";
import { STRING_CONSTANTS } from "../../utils/constants/stringConstants";
import { Helmet } from 'react-helmet';
import { userService, cookieService } from "../../_services";
import "./InterviewSimulatorSignup.css";

interface InterviewSimulatorSignupProps extends RouteComponentProps<any> {}

const InterviewSimulatorSignup: React.FC<InterviewSimulatorSignupProps> = (props) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [affiliateCode, setAffiliateCode] = useState(localStorage.getItem("referralCode") || '');
  const [accountType, setAccountType] = useState<'company' | 'candidate'>('company');
  const [pwdType, setPwdType] = useState("password");
  const [btnClick, setBtnClick] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [mobileNumber, setMobileNumber] = useState('');

  const hasNumber = /\d/;
  const isEmailValid = UtilityFunctions.validateEmail(email);
  const isPasswordValid = password.length >= 8 && hasNumber.test(password) && !UtilityFunctions.checkSpaceInPassword(password);
  const isMobileValid = !UtilityFunctions.isEmptyOrNullOrUndefined(mobileNumber);
  const isFormValid = isEmailValid && isPasswordValid && isMobileValid;

  const togglePassword = () => {
    setPwdType(pwdType === "password" ? "text" : "password");
  };

  const scrollToForm = () => {
    document.getElementById('is-signup-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBtnClick(true);
    setApiError(null);

    if (!isFormValid) return;

    setIsLoading(true);
    try {
      const signupCode = affiliateCode.trim();
      const signupData: any = {
        email,
        password,
        mobileNumber,
        type: 'Mploi-er',
        account_type: accountType,
        accountType,
        is_company_signup: accountType === 'company',
        is_candidate_signup: accountType === 'candidate',
        is_interview_simulator_signup: true,
        isAccountsSignup: true,
        is_accounts_signup: true,
        signup_code: signupCode,
        signupCode,
        referred_by_code: signupCode,
        referredByCode: signupCode,
      };

      await userService.signup(signupData, signupCode, true);

      const loginResponse = await userService.login({ email, password });

      if (loginResponse.data && loginResponse.data.user && loginResponse.data.user.token) {
        localStorage.setItem('access_token', loginResponse.data.user.token.access);
        localStorage.setItem('refresh_token', loginResponse.data.user.token.refresh);
        localStorage.setItem('user_data', JSON.stringify(loginResponse.data.user));
        localStorage.setItem('isGuest', 'false');

        cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.token, loginResponse.data.user.token.access);
        cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.user, loginResponse.data.user);
        cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.isGuest, false);

        // Mark profile as complete so PrivateRoute doesn't redirect to profilecompletion
        await userService.userProfile({ user: { isProfileCompleted: true } });
        await userService.getUserData();
        props.history.push('/interview-simulator/billing');
      }
    } catch (error: any) {
      console.error("Signup failed:", error);
      if (error && Array.isArray(error) && error[0] === 'Email') {
        setApiError("Email already exists");
      } else {
        setApiError(
          error.response?.data?.message ||
          error.message ||
          "An unexpected error occurred during signup."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const terms = { pathname: `/terms`, state: {} };
  const privacy = { pathname: `/privacy`, state: {} };

  return (
    <Fragment>
      <Helmet>
        <title>MPLOI Interview Simulator - Smarter Interviews for Everyone</title>
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Urbanist:wght@400;500;700;800&display=swap" rel="stylesheet" />
      </Helmet>

      <div className="is-signup">
        {/* Navigation */}
        <nav className="is-nav">
          <img
            src={UtilityFunctions.getImageURL() + "logo_White.svg"}
            alt="MPLOI Logo"
            className="is-nav-logo"
            onClick={() => props.history.push("/")}
          />
          <ul className="is-nav-links">
            <li><a href="https://mploi.com/#about">About Us</a></li>
            <li><a href="https://mploi.com/#services">Services</a></li>
            <li><a href="https://mploi.com/blog">Blog</a></li>
            <li><a href="https://mploi.com/#pricing">Pricing</a></li>
            <li><a href="https://mploi.com/#contact">Contact</a></li>
          </ul>
          <div className="is-nav-actions">
            <button className="is-btn-login" onClick={() => window.location.href = '/signin'}>Log In</button>
            <button className="is-btn-get-started" onClick={scrollToForm}>Get Started</button>
          </div>
        </nav>

        {/* Hero */}
        <section className="is-hero">
          <div className="is-hero-content">
            <h1>AI Interview<br/>Simulator</h1>
            <p className="is-hero-tagline">Smarter interviews. Better outcomes — for everyone.</p>
            <p className="is-hero-subtitle">
              Whether you're a job seeker building confidence or a business streamlining hiring, 
              MPLOI Interview Simulator delivers intelligent, unbiased interviews at scale — 
              transforming how talent meets opportunity.
            </p>
            <button className="is-hero-cta" onClick={scrollToForm}>
              Join NOW <span style={{fontSize: '20px'}}>↗</span>
            </button>
          </div>
        </section>

        {/* Content */}
        <section className="is-content">
          <div className="is-content-inner">
            <span className="is-section-label">About the Simulator</span>
            <h2>The interview process is broken — for everyone involved.</h2>
            <div className="is-content-body">
              <p>
                Job seekers spend weeks anxious and underprepared, while businesses lose countless hours 
                scheduling, conducting, and second-guessing candidate selections.
              </p>
              <p>
                <strong>MPLOI Interview Simulator fixes both sides of that equation at once.</strong>
              </p>
              <p>
                For businesses, it's a fully white-label recruitment powerhouse. Deploy custom-branded AI 
                interviews at scale, eliminate scheduling bottlenecks, and receive comprehensive candidate 
                reports delivered directly to your team — all without lifting a finger. No bias. No delays. 
                No wasted resources.
              </p>
              <p>
                For job seekers, it's a realistic, judgment-free environment where you practice with a 
                lifelike AI Avatar, receive instant personalised feedback, and build the genuine confidence 
                needed to perform when it counts. Every session produces a detailed performance report — 
                tracking your progress, reinforcing your strengths, and targeting the areas that will make 
                the difference between a shortlist and a job offer.
              </p>
              <p>
                Whether you're preparing to land your dream role or building a hiring process that actually 
                works, MPLOI Interview Simulator meets you exactly where you are — intelligent, 
                inclusive, and built for the way the world works today.
              </p>
              <p className="is-closing">
                One platform. Smarter interviews. Better outcomes — for everyone.
              </p>
            </div>
          </div>
        </section>

        {/* Signup Form */}
        <section className="is-form-section" id="is-signup-form">
          <div className="is-form-container">
            <h2 className="is-form-title">Create Your Account</h2>

            <form onSubmit={handleSubmit} noValidate>
              <div className="is-form-group">
                <label style={{ display: 'block', color: '#FFFFFF', fontWeight: 700, marginBottom: '10px', textAlign: 'left' }}>I am signing up as</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => setAccountType('company')}
                    style={{
                      padding: '14px 16px',
                      borderRadius: '12px',
                      border: accountType === 'company' ? '2px solid #FFFFFF' : '1px solid rgba(255,255,255,0.35)',
                      background: accountType === 'company' ? '#0065FF' : 'rgba(255,255,255,0.08)',
                      color: '#FFFFFF',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Company
                  </button>
                  <button
                    type="button"
                    onClick={() => setAccountType('candidate')}
                    style={{
                      padding: '14px 16px',
                      borderRadius: '12px',
                      border: accountType === 'candidate' ? '2px solid #FFFFFF' : '1px solid rgba(255,255,255,0.35)',
                      background: accountType === 'candidate' ? '#0065FF' : 'rgba(255,255,255,0.08)',
                      color: '#FFFFFF',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Candidate
                  </button>
                </div>
              </div>

              <div className="is-form-group">
                <input
                  type="email"
                  placeholder="Email Address"
                  className={`is-form-input ${btnClick && !isEmailValid ? 'error' : ''}`}
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setBtnClick(false); setApiError(null); }}
                  disabled={isLoading}
                  required
                />
                {btnClick && !isEmailValid && <div className="is-form-error">Please enter a valid email address</div>}
              </div>

              <div className="is-form-group">
                <input
                  type="tel"
                  placeholder="Mobile Phone"
                  className={`is-form-input ${btnClick && !isMobileValid ? 'error' : ''}`}
                  value={mobileNumber}
                  onChange={(e) => { setMobileNumber(e.target.value); setBtnClick(false); setApiError(null); }}
                  disabled={isLoading}
                  required
                />
                {btnClick && !isMobileValid && <div className="is-form-error">Please enter your mobile number</div>}
              </div>

              <div className="is-form-group">
                <input
                  type={pwdType}
                  placeholder="Password"
                  className={`is-form-input ${btnClick && !isPasswordValid ? 'error' : ''}`}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setBtnClick(false); setApiError(null); }}
                  disabled={isLoading}
                  required
                />
                <div className="is-form-icon" onClick={togglePassword}>
                  <img
                    src={pwdType === "password" ?
                      UtilityFunctions.getImageURL() + 'eyeOff.png' :
                      UtilityFunctions.getImageURL() + 'eyeOn.png'}
                    alt="Toggle password"
                  />
                </div>
                {btnClick && !isPasswordValid && <div className="is-form-error">Password must meet the requirements below</div>}
              </div>

              <div className="is-form-group">
                <input
                  type="text"
                  placeholder="Demo Code (Optional)"
                  className="is-form-input"
                  value={affiliateCode}
                  onChange={(e) => setAffiliateCode(e.target.value)}
                  disabled={isLoading || !!localStorage.getItem("referralCode")}
                />
              </div>

              <ul className="is-validation-list">
                <li className={`is-validation-item ${password.length <= 0 ? '' : password.length >= 8 && !UtilityFunctions.checkSpaceInPassword(password) ? 'valid' : 'invalid'}`}>
                  ✓ {STRING_CONSTANTS.validationString.min_chars}
                </li>
                <li className={`is-validation-item ${password.length <= 0 ? '' : hasNumber.test(password) ? 'valid' : 'invalid'}`}>
                  ✓ {STRING_CONSTANTS.validationString.number_req}
                </li>
              </ul>

              {apiError && <div className="is-api-error">{apiError}</div>}

              <button
                type="submit"
                className="is-submit-btn"
                disabled={!isFormValid || isLoading}
              >
                {isLoading ? (
                  <span><span className="is-spinner"></span>Creating Account...</span>
                ) : "GET STARTED NOW"}
              </button>

              <div className="is-form-footer">
                <p>
                  By signing up, you agree to our{" "}
                  <Link to={terms}>Terms of Service</Link>{" "}and{" "}
                  <Link to={privacy}>Privacy Policy</Link>
                </p>
                <p style={{marginTop: '16px'}}>
                  Already have an account?{" "}
                  <a href="/signin">Sign In</a>
                </p>
              </div>
            </form>
          </div>
        </section>

        {/* Footer */}
        <footer className="is-footer">
          <p>Questions? Contact our support team</p>
          <p><a href="mailto:info@mploi.com">info@mploi.com</a></p>
          <p style={{marginTop: '16px'}}>
            <Link to={privacy}>Privacy Policy</Link>{" | "}
            <Link to={terms}>Terms & Conditions</Link>
          </p>
          <p style={{marginTop: '24px', fontSize: '12px'}}>
            All rights reserved. Copyright © {new Date().getFullYear()} MPLOI
          </p>
        </footer>
      </div>
    </Fragment>
  );
};

export default withRouter(InterviewSimulatorSignup);
